/* Shared identifier parsing. Matches remain tied to actual native product variants. */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.SaberCatalogSearch = api;
})(typeof window === "undefined" ? globalThis : window, function () {
  "use strict";

  const normalizeSku = (value) =>
    String(value || "")
      .trim()
      .toLocaleUpperCase("en-US");

  function parseSku(value) {
    const full = String(value || "").trim();
    const parts = { item: [], model: [] };
    const segments = full
      .split("|")
      .map((segment) => segment.trim())
      .filter(Boolean);
    let complete = segments.length > 0;
    for (const segment of segments) {
      const match = /^(item|model)\s*#\s*(.+)$/i.exec(segment);
      if (!match) {
        complete = false;
        continue;
      }
      parts[match[1].toLowerCase()].push(match[2].trim());
    }
    if (parts.item.length > 1 || parts.model.length > 1) complete = false;
    return { full, parts, complete };
  }

  function skuAliases(value) {
    const parsed = parseSku(value);
    const aliases = [parsed.full];
    if (parsed.parts.item.length === 1) aliases.push(parsed.parts.item[0]);
    if (parsed.parts.model.length === 1) aliases.push(parsed.parts.model[0]);
    const seen = new Set();
    return aliases.filter((alias) => {
      const key = normalizeSku(alias);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function isExactSkuMatch(stored, entered) {
    const actual = parseSku(stored),
      input = parseSku(entered);
    if (!input.full || !actual.full) return false;
    if (normalizeSku(actual.full) === normalizeSku(input.full)) return true;
    if (input.complete) {
      const keys = ["item", "model"].filter((key) => input.parts[key].length);
      if (!keys.length) return false;
      // A copied item/model pair must match every supplied component of its own type.
      if (actual.complete)
        return keys.every(
          (key) =>
            actual.parts[key].length === 1 &&
            normalizeSku(actual.parts[key][0]) ===
              normalizeSku(input.parts[key][0]),
        );
      return (
        keys.length === 1 &&
        normalizeSku(actual.full) === normalizeSku(input.parts[keys[0]][0])
      );
    }
    // Mixed unrecognized label text cannot silently collapse to one matching component.
    if (input.parts.item.length || input.parts.model.length) return false;
    return skuAliases(stored).some(
      (alias) => normalizeSku(alias) === normalizeSku(input.full),
    );
  }

  function escapeQuery(value) {
    return String(value || "")
      .trim()
      .replace(/[\\:()"'*]/g, "\\$&")
      .replace(/^-/, "\\-");
  }

  function buildSearchQuery(value) {
    const parsed = parseSku(value);
    const term = parsed.complete
      ? parsed.parts.item[0] || parsed.parts.model[0] || parsed.full
      : parsed.full;
    return escapeQuery(term);
  }

  function isIdentifierQuery(value) {
    const parsed = parseSku(value);
    const term = parsed.complete
      ? parsed.parts.item[0] || parsed.parts.model[0] || ""
      : parsed.full;
    return (
      term.length >= 3 &&
      term.length <= 120 &&
      /\d/.test(term) &&
      !/\s/.test(term)
    );
  }

  function prefixQueries(value) {
    const parsed = parseSku(value);
    const term = parsed.complete ? parsed.parts.item[0] || "" : parsed.full;
    // These two label spacings occur in this store's real SKUs. Native middle/leading
    // wildcards and OR expansion are deliberately excluded: they broaden real results.
    return /^\d{3,18}$/.test(term) ? ["Item #" + term, "Item # " + term] : [];
  }

  function isSkuPrefixMatch(stored, entered) {
    const parsed = parseSku(entered);
    const term = parsed.complete
      ? parsed.parts.item[0] || parsed.parts.model[0] || ""
      : parsed.full;
    if (!isIdentifierQuery(term)) return false;
    return skuAliases(stored).some((alias) =>
      normalizeSku(alias).startsWith(normalizeSku(term)),
    );
  }

  return {
    normalizeSku,
    parseSku,
    skuAliases,
    isExactSkuMatch,
    buildSearchQuery,
    isIdentifierQuery,
    prefixQueries,
    isSkuPrefixMatch,
  };
});
